import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calculator } from 'lucide-react';
import useIsMobile from '../../hooks/useIsMobile';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import sessionService from '../../services/sessionService';
import fournisseurService from '../../services/fournisseurService';
import centresService from '../../services/centresService';
import { formatMoney } from '../../utils/money';
import '../Sessions/SessionsPage.css';
import './ComptabilitePage.css';

const isoDate = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const firstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const lastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};

const statusBadgeStyle = (status) => {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.15rem 0.5rem',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '0.75rem',
    border: '1px solid transparent',
    whiteSpace: 'nowrap',
  };
  const s = String(status || '').toUpperCase();

  if (s === 'CANCELLED' || s === 'REFUSE' || s === 'REJECTED') {
    return { ...base, background: '#FEE2E2', color: '#991B1B', borderColor: '#FCA5A5' };
  }
  if (s === 'NEW' || s === 'EN_ATTENTE') {
    return { ...base, background: '#FEF3C7', color: '#92400E', borderColor: '#FCD34D' };
  }
  if (s === 'ACCEPTED' || s === 'VERIFIE' || s === 'SHIPPED') {
    return { ...base, background: '#DBEAFE', color: '#1E40AF', borderColor: '#93C5FD' };
  }
  if (s === 'RECEIVED' || s === 'VALIDE' || s === 'INVOICED') {
    return { ...base, background: '#D1FAE5', color: '#065F46', borderColor: '#6EE7B7' };
  }
  return { ...base, background: '#E5E7EB', color: '#111827', borderColor: '#D1D5DB' };
};

const kpiCardStyle = {
  flex: '1 1 220px',
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  background: '#FFFFFF',
  padding: '0.9rem',
};

const filterBarStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  width: '100%',
};

const filterRow1Style = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1.5fr',
  gap: '0.75rem',
  alignItems: 'end',
  width: '100%',
};

const filterRow2Style = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr auto',
  gap: '0.75rem',
  alignItems: 'end',
  width: '100%',
};

const filterLabelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: '#6B7280',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  marginBottom: 6,
};

const filterButtonStyle = {
  width: 140,
  minWidth: 140,
  height: 42,
  flex: '0 0 auto',
};

const dateInputStyle = {
  height: 42,
  lineHeight: '42px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  paddingRight: 46,
  boxSizing: 'border-box',
};

const controlStyle = {
  height: 42,
  minHeight: 42,
};

const ComptabilitePage = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { user, isAdmin } = useAuth();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(false);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [centres, setCentres] = useState([]);

  const [dateFrom, setDateFrom] = useState(isoDate(firstDayOfMonth()));
  const [dateTo, setDateTo] = useState(isoDate(lastDayOfMonth()));
  const [selectedCentreIds, setSelectedCentreIds] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [feeStatus, setFeeStatus] = useState('');

  const [expandedOrderIds, setExpandedOrderIds] = useState(() => new Set());

  const [report, setReport] = useState(null);
  const [didAutoLoad, setDidAutoLoad] = useState(false);

  const availableCentres = useMemo(() => {
    if (Array.isArray(centres) && centres.length > 0) return centres;
    return [];
  }, [centres]);

  const fetchRefs = async () => {
    try {
      try {
        const supplierResp = await fournisseurService.getAll({ page_size: 200 });
        const supplierData = Array.isArray(supplierResp)
          ? supplierResp
          : (supplierResp?.results || supplierResp?.data?.results || supplierResp?.data || []);
        const list = Array.isArray(supplierData) ? supplierData : [];
        setFournisseurs(list);
        console.log('ComptabilitePage fournisseurs loaded:', list.length);
      } catch (e) {
        setFournisseurs([]);
      }

      if (Array.isArray(user?.centres_details) && user.centres_details.length > 0) {
        setCentres(user.centres_details);
        setSelectedCentreIds(user.centres_details.map((c) => c.id));
        return;
      }

      if (user?.centre && typeof user.centre === 'string') {
        const centreResp = await centresService.getAll();
        const centresData = Array.isArray(centreResp) ? centreResp : (centreResp?.results || centreResp?.centres || []);
        const filtered = (centresData || []).filter((c) => String(c.id) === String(user.centre));
        setCentres(filtered);
        setSelectedCentreIds(filtered.map((c) => c.id));
        return;
      }

      if (isAdmin) {
        const centreResp = await centresService.getAll();
        const centresData = Array.isArray(centreResp) ? centreResp : (centreResp?.results || centreResp?.centres || []);
        setCentres(Array.isArray(centresData) ? centresData : []);
      }
    } catch (e) {
      console.error('Error fetching compta refs:', e);
      toast.error("Impossible de charger les référentiels");
    }
  };

  const runReport = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Veuillez renseigner une période');
      return;
    }

    try {
      setLoading(true);
      const resp = await sessionService.accountingReport({
        date_from: dateFrom,
        date_to: dateTo,
        centre_ids: selectedCentreIds,
        supplier_id: selectedSupplierId,
        order_status: orderStatus,
        fee_status: feeStatus,
      });
      setReport(resp);
      try {
        const o = resp?.orders?.length || 0;
        const f = resp?.additional_fees?.length || 0;
        const s = resp?.totals_by_supplier?.length || 0;
        console.log('Accounting report:', { orders: o, additional_fees: f, totals_by_supplier: s });
      } catch (e2) {
        // ignore
      }
    } catch (e) {
      console.error('Error fetching accounting report:', e);
      const msg = e?.response?.data?.detail;
      toast.error(msg || "Impossible de charger le reporting");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefs();
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) return;
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const firstDay = `${yyyy}-${mm}-01`;
    const today = `${yyyy}-${mm}-${dd}`;
    setDateFrom((prev) => prev || firstDay);
    setDateTo((prev) => prev || today);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (selectedCentreIds.length === 0 && availableCentres.length > 0) {
      setSelectedCentreIds(availableCentres.map((c) => c.id));
    }
  }, [availableCentres, selectedCentreIds.length]);

  useEffect(() => {
    if (didAutoLoad) return;
    if (!dateFrom || !dateTo) return;
    if (availableCentres.length > 0 && selectedCentreIds.length === 0) return;

    setDidAutoLoad(true);
    runReport();
  }, [didAutoLoad, dateFrom, dateTo, availableCentres.length, selectedCentreIds.length]);

  const totals = useMemo(() => report?.totals_by_supplier || [], [report]);
  const orders = useMemo(() => report?.orders || [], [report]);
  const fees = useMemo(() => report?.additional_fees || [], [report]);

  const feesBySessionAndSupplier = useMemo(() => {
    const map = new Map();
    for (const f of fees) {
      const sid = String(f?.session_id || '');
      const supId = String(f?.supplier_id || '');
      const key = `${sid}__${supId}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(f);
    }
    for (const [key, list] of map.entries()) {
      list.sort((a, b) => {
        const da = a?.fee_date ? new Date(a.fee_date).getTime() : 0;
        const db = b?.fee_date ? new Date(b.fee_date).getTime() : 0;
        return db - da;
      });
      map.set(key, list);
    }
    return map;
  }, [fees]);

  const isEmptyReport = !!report && totals.length === 0 && orders.length === 0 && fees.length === 0;

  const defaultCurrency = user?.centres_details?.[0]?.devise || 'EUR';

  const getOrderCurrency = useCallback((order) => (
    order?.currency || order?.supplier_devise || order?.centre_devise || defaultCurrency
  ), [defaultCurrency]);

  const getFeeCurrency = useCallback((fee) => (
    fee?.currency || fee?.supplier_devise || fee?.centre_devise || defaultCurrency
  ), [defaultCurrency]);

  const buildTotalsByCurrency = useCallback((items, getCurrency, getAmount) => {
    const map = new Map();
    for (const item of items) {
      const currency = getCurrency(item);
      const current = map.get(currency) || 0;
      map.set(currency, current + Number(getAmount(item) || 0));
    }
    return Array.from(map.entries()).map(([currency, amount]) => ({ currency, amount }));
  }, []);

  const ordersTotalsByCurrency = useMemo(() => (
    buildTotalsByCurrency(orders, getOrderCurrency, (o) => o?.total_amount)
  ), [orders, buildTotalsByCurrency, getOrderCurrency]);

  const feesTotalsByCurrency = useMemo(() => (
    buildTotalsByCurrency(fees, getFeeCurrency, (f) => f?.amount)
  ), [fees, buildTotalsByCurrency, getFeeCurrency]);

  const totalsByCurrency = useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const currency = getOrderCurrency(o);
      const current = map.get(currency) || 0;
      map.set(currency, current + Number(o?.total_amount || 0));
    }
    for (const f of fees) {
      const currency = getFeeCurrency(f);
      const current = map.get(currency) || 0;
      map.set(currency, current + Number(f?.amount || 0));
    }
    return Array.from(map.entries()).map(([currency, amount]) => ({ currency, amount }));
  }, [orders, fees, getOrderCurrency, getFeeCurrency]);

  const supplierCount = useMemo(() => {
    return new Set(totals.map((r) => r?.supplier_id).filter(Boolean)).size;
  }, [totals]);

  const formatByCurrency = (amount, currency) => {
    return formatMoney(amount, { locale: i18n.language, currency: currency || 'EUR' });
  };

  const toggleCentre = (centreId) => {
    setSelectedCentreIds((prev) => {
      const s = new Set(prev.map(String));
      const id = String(centreId);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return Array.from(s);
    });
  };

  const toggleOrderExpanded = (orderId) => {
    setExpandedOrderIds((prev) => {
      const next = new Set(prev);
      const id = String(orderId);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="sessions-page comptabilite-page">
      <div className="page-header-section">
        <div className="header-titles">
          <h1 className="page-title">
            <Calculator size={32} />
            Comptabilité
          </h1>
          <p className="page-subtitle">Reporting par fournisseur sur une période (sessions validées + frais additionnels)</p>
        </div>
      </div>

      <div className="filters-section">
        <div style={filterBarStyle}>
          <div style={isMobile ? { ...filterRow1Style, gridTemplateColumns: '1fr' } : filterRow1Style}>
            <div>
              <div style={filterLabelStyle}>Du</div>
              <div className="search-bar" style={{ width: '100%', maxWidth: '100%' }}>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="search-input"
                  style={{ ...dateInputStyle, ...controlStyle }}
                />
              </div>
            </div>

            <div>
              <div style={filterLabelStyle}>Au</div>
              <div className="search-bar" style={{ width: '100%', maxWidth: '100%' }}>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="search-input"
                  style={{ ...dateInputStyle, ...controlStyle }}
                />
              </div>
            </div>

            <div>
              <div style={filterLabelStyle}>Fournisseur</div>
              <select
                className="ui-select"
                value={selectedSupplierId}
                onChange={(e) => setSelectedSupplierId(e.target.value)}
                style={controlStyle}
              >
                <option value="">Tous</option>
                {fournisseurs.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={isMobile ? { ...filterRow2Style, gridTemplateColumns: '1fr 1fr' } : filterRow2Style}>
            <div>
              <div style={filterLabelStyle}>Statut commande</div>
              <select className="ui-select" value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)} style={controlStyle}>
                <option value="">Tous</option>
                <option value="NEW">Nouvelle</option>
                <option value="ACCEPTED">Acceptée</option>
                <option value="SHIPPED">Expédiée</option>
                <option value="RECEIVED">Reçue</option>
                <option value="INVOICED">Bon de commande</option>
                <option value="CANCELLED">Annulée</option>
              </select>
            </div>

            <div>
              <div style={filterLabelStyle}>Statut frais</div>
              <select className="ui-select" value={feeStatus} onChange={(e) => setFeeStatus(e.target.value)} style={controlStyle}>
                <option value="">Tous</option>
                <option value="EN_ATTENTE">En attente</option>
                <option value="VERIFIE">Vérifié</option>
                <option value="VALIDE">Validé</option>
                <option value="REFUSE">Refusé</option>
              </select>
            </div>

            <div
              style={isMobile
                ? { display: 'flex', justifyContent: 'stretch', gridColumn: '1 / -1' }
                : { display: 'flex', justifyContent: 'flex-end' }}
            >
              <button
                type="button"
                className="btn-primary"
                onClick={runReport}
                disabled={loading}
                style={isMobile ? { ...filterButtonStyle, width: '100%', minWidth: 0 } : filterButtonStyle}
              >
                {loading ? t('common.loading') : 'Lancer'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {availableCentres.length > 0 && (
        <div className="filters-section" style={{ gap: '0.75rem', justifyContent: 'flex-start' }}>
          <div style={{ fontWeight: 600 }}>Centres</div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {availableCentres.map((c) => {
              const checked = selectedCentreIds.map(String).includes(String(c.id));
              return (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleCentre(c.id)} />
                  <span>{c.name}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {report && (
        <div className="table-container" style={{ marginTop: '1rem' }}>
          <div style={{ padding: '0.9rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={kpiCardStyle}>
                <div style={{ color: '#6B7280', fontWeight: 700, fontSize: '0.85rem' }}>Total commandes</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.25rem' }}>
                  {ordersTotalsByCurrency.length === 0 ? (
                    <div style={{ fontSize: '1.35rem', fontWeight: 900 }}>-</div>
                  ) : (
                    ordersTotalsByCurrency.map(({ currency, amount }) => (
                      <div key={currency} style={{ fontSize: '1.35rem', fontWeight: 900 }}>
                        {formatByCurrency(amount, currency)}
                      </div>
                    ))
                  )}
                </div>
                <div style={{ color: '#6B7280', marginTop: '0.25rem' }}>{orders.length} commande(s)</div>
              </div>
              <div style={kpiCardStyle}>
                <div style={{ color: '#6B7280', fontWeight: 700, fontSize: '0.85rem' }}>Total frais</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.25rem' }}>
                  {feesTotalsByCurrency.length === 0 ? (
                    <div style={{ fontSize: '1.35rem', fontWeight: 900 }}>-</div>
                  ) : (
                    feesTotalsByCurrency.map(({ currency, amount }) => (
                      <div key={currency} style={{ fontSize: '1.35rem', fontWeight: 900 }}>
                        {formatByCurrency(amount, currency)}
                      </div>
                    ))
                  )}
                </div>
                <div style={{ color: '#6B7280', marginTop: '0.25rem' }}>{fees.length} frais</div>
              </div>
              <div style={{ ...kpiCardStyle, borderColor: '#D1D5DB', background: '#F9FAFB' }}>
                <div style={{ color: '#374151', fontWeight: 800, fontSize: '0.85rem' }}>Total global</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.25rem' }}>
                  {totalsByCurrency.length === 0 ? (
                    <div style={{ fontSize: '1.35rem', fontWeight: 900 }}>-</div>
                  ) : (
                    totalsByCurrency.map(({ currency, amount }) => (
                      <div key={currency} style={{ fontSize: '1.35rem', fontWeight: 900 }}>
                        {formatByCurrency(amount, currency)}
                      </div>
                    ))
                  )}
                </div>
                <div style={{ color: '#6B7280', marginTop: '0.25rem' }}>{supplierCount} fournisseur(s)</div>
              </div>
            </div>
            {isEmptyReport && (
              <div style={{ marginTop: '0.9rem', padding: '0.75rem', borderRadius: 12, background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <div style={{ fontWeight: 900, color: '#92400E' }}>Aucun résultat sur la période</div>
                <div style={{ marginTop: '0.35rem', color: '#92400E' }}>
                  Les commandes sont prises en compte uniquement si la <strong>session est validée</strong> (filtre sur <code>session.validated_at</code>). Vérifie qu’au moins une session est validée dans cet intervalle, ou élargis les dates.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {report && (
        <div className="table-container" style={{ marginTop: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0.75rem 0' }}>
            <h3 style={{ margin: 0 }}>Totaux par fournisseur</h3>
            <div style={{ fontWeight: 700, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {totalsByCurrency.map(({ currency, amount }) => (
                <div key={currency}>{formatByCurrency(amount, currency)}</div>
              ))}
            </div>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Fournisseur</th>
                <th>Devise</th>
                <th>Total commandes</th>
                <th>Total frais</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {totals.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }}>Aucun résultat</td>
                </tr>
              ) : (
                totals.map((r, idx) => (
                  <tr key={`${r.supplier_id || 'supplier'}__${r.currency || 'currency'}`} style={{ background: idx % 2 ? '#FAFAFA' : 'transparent' }}>
                    <td>{r.supplier_name || '-'}</td>
                    <td>{r.currency || '-'}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatByCurrency(r.orders_total, r.currency)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatByCurrency(r.fees_total, r.currency)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}><strong>{formatByCurrency(r.grand_total, r.currency)}</strong></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {report && (
        <div className="table-container" style={{ marginTop: '1rem' }}>
          <div style={{ padding: '0.75rem 0.75rem 0' }}>
            <h3 style={{ margin: 0 }}>Commandes (sessions validées)</h3>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Session</th>
                <th>Centre</th>
                <th>Fournisseur</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Date session validée</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '1.5rem' }}>Aucune commande</td>
                </tr>
              ) : (
                orders.flatMap((o, idx) => {
                  const key = `${String(o?.session_id || '')}__${String(o?.supplier_id || '')}`;
                  const rowFees = feesBySessionAndSupplier.get(key) || [];
                  const isExpanded = expandedOrderIds.has(String(o?.order_id));
                  const rowBg = idx % 2 ? '#FAFAFA' : 'transparent';

                  return [
                    (
                      <tr key={o.order_id} style={{ background: rowBg }}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>{o.formation_title || '-'}</span>
                            {rowFees.length > 0 && (
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => toggleOrderExpanded(o.order_id)}
                                style={{
                                  fontWeight: 800,
                                  color: '#111827',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: 999,
                                  padding: '0.1rem 0.5rem',
                                  background: '#FFFFFF',
                                }}
                                title="Afficher les frais additionnels"
                              >
                                Frais ({rowFees.length})
                              </button>
                            )}
                          </div>
                        </td>
                        <td>{o.centre_name || '-'}</td>
                        <td>{o.supplier_name || '-'}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatByCurrency(o.total_amount, getOrderCurrency(o))}</td>
                        <td><span style={statusBadgeStyle(o.status)}>{o.status || '-'}</span></td>
                        <td>{o.validated_at ? new Date(o.validated_at).toLocaleDateString(i18n.language) : '-'}</td>
                        <td className="actions-column">
                          <div className="action-buttons">
                            <Link to={`/mes-demandes/${o.order_id}`} className="btn-icon" title="Voir la demande">Voir</Link>
                            <Link to={`/sessions/${o.session_id}`} className="btn-icon" title="Voir la session">Session</Link>
                          </div>
                        </td>
                      </tr>
                    ),
                    (rowFees.length > 0 && isExpanded) ? (
                      <tr key={`${o.order_id}__fees`} style={{ background: rowBg }}>
                        <td colSpan={7} style={{ padding: '0.75rem 0.75rem 1rem' }}>
                          <div style={{
                            border: '1px solid #E5E7EB',
                            borderRadius: 12,
                            background: '#FFFFFF',
                            overflow: 'hidden',
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '0.6rem 0.75rem',
                              background: '#F9FAFB',
                              borderBottom: '1px solid #E5E7EB',
                            }}>
                              <div style={{ fontWeight: 900 }}>Frais additionnels</div>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => toggleOrderExpanded(o.order_id)}
                                title="Fermer"
                              >
                                Fermer
                              </button>
                            </div>
                            <table className="data-table" style={{ margin: 0 }}>
                              <thead>
                                <tr>
                                  <th>Libellé</th>
                                  <th>Montant</th>
                                  <th>Date</th>
                                  <th>Statut</th>
                                  <th className="actions-column">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rowFees.map((f) => (
                                  <tr key={f.additional_fee_id}>
                                    <td>{f.label || '-'}</td>
                                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatByCurrency(f.amount, getFeeCurrency(f))}</td>
                                    <td>{f.fee_date ? new Date(f.fee_date).toLocaleDateString(i18n.language) : '-'}</td>
                                    <td><span style={statusBadgeStyle(f.status)}>{f.status || '-'}</span></td>
                                    <td className="actions-column">
                                      <div className="action-buttons">
                                        <Link to={`/sessions/${f.session_id}#additional-fees`} className="btn-icon" title="Voir la session">Session</Link>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    ) : null,
                  ].filter(Boolean);
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ComptabilitePage;
